import React, { Component } from "react";
import { Link } from "react-router-dom";
import HelperClass from "../../../config/helperClass";
import { THEME_COLOR } from "../../../config/config";
import { getFunnelMetrics, getPipelineKpis, getBreakdowns } from "./api/leads_api";
import { LEAD_SOURCES } from "./api/leads_constants";

const auth = new HelperClass();

function fmtMoney(n) {
  var v = Number(n) || 0;
  if (v >= 1000000) return "$" + (v / 1000000).toFixed(1) + "M";
  if (v >= 1000) return "$" + (v / 1000).toFixed(1) + "k";
  return "$" + v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/* ─── Score band helpers ──────────────────────────────────────────────── */
var SCORE_BANDS = [
  { min: 70, max: 100, label: "High", color: "#1a7a4a", bg: "#e6f4ec" },
  { min: 40, max: 69,  label: "Medium", color: "#d97706", bg: "#fef3c7" },
  { min: 0,  max: 39,  label: "Low",  color: "#6b7280", bg: "#f3f4f6" },
];

export default class LeadsFunnel extends Component {
  state = { kpis: null, funnel: [], byPartner: [], bySource: [], loading: true };

  async componentDidMount() {
    var acc = auth.getAccount();
    var tok = auth.getToken();
    try {
      var results = await Promise.all([
        getPipelineKpis(acc, tok, {}),
        getFunnelMetrics(acc, tok, {}),
        getBreakdowns(acc, tok, {}),
      ]);
      var k = results[0], f = results[1], b = results[2];
      this.setState({
        kpis:      k && k.data && k.data.status === 200 ? k.data.data : null,
        funnel:    f && f.data && f.data.status === 200 ? f.data.data : [],
        byPartner: b && b.data && b.data.status === 200 ? b.data.data.by_partner : [],
        bySource:  b && b.data && b.data.status === 200 ? b.data.data.by_source  : [],
        loading: false,
      });
    } catch (_) {
      this.setState({ loading: false });
    }
  }

  render() {
    var { kpis, funnel, byPartner, bySource, loading } = this.state;

    /* ─── Funnel split: active stages vs Won/Lost ─────────────────── */
    var activeFunnel = funnel.filter(function(r) { return !r.stage.is_won && !r.stage.is_lost; });
    var wonRows      = funnel.filter(function(r) { return r.stage.is_won; });
    var lostRows     = funnel.filter(function(r) { return r.stage.is_lost; });
    var maxCount     = Math.max(1, activeFunnel.reduce(function(acc, r) { return Math.max(acc, r.count); }, 0));

    /* ─── By Source: all LEAD_SOURCES + any extra from backend ───── */
    var sourceMap = {};
    bySource.forEach(function(s) { sourceMap[s.source] = s; });
    var allSources = LEAD_SOURCES.map(function(src) {
      return sourceMap[src] || { source: src, count: 0, value: 0 };
    });
    bySource.forEach(function(s) {
      if (LEAD_SOURCES.indexOf(s.source) === -1) allSources.push(s);
    });
    var sourceMax = Math.max(1, allSources.reduce(function(acc, s) { return Math.max(acc, s.count); }, 0));

    /* ─── Won totals ─────────────────────────────────────────────── */
    var wonTotal = wonRows.reduce(function(a, r) { return { count: a.count + r.count, value: a.value + r.value }; }, { count: 0, value: 0 });
    var lostTotal = lostRows.reduce(function(a, r) { return { count: a.count + r.count, value: a.value + r.value }; }, { count: 0, value: 0 });

    return (
      <div className="nk-content">
        <div className="container-fluid">
          <div className="nk-content-inner">
            <div className="nk-content-body">

              {/* ── page header ── */}
              <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                  <div className="nk-block-head-content">
                    <h3 className="nk-block-title page-title">Sales Funnel</h3>
                    <div className="nk-block-des text-soft">
                      <p>End-to-end lead lifecycle — from first touch to converted client.</p>
                    </div>
                  </div>
                  <div className="nk-block-head-content" id="HeadContentFunnel">
                    <div className="toggle-wrap nk-block-tools-toggle">
                      <a href="#" className="btn btn-icon btn-trigger toggle-expand mr-n1" data-target="funnelMenu">
                        <em className="icon ni ni-more-v"></em>
                      </a>
                      <div className="toggle-expand-content" data-content="funnelMenu">
                        <ul className="nk-block-tools g-3">
                          <li className="nk-block-tools-opt">
                            <Link to="/crm_leads_kanban" className="btn btn-info" style={{ color: "#fff" }}>
                              <em className="icon ni ni-layout"></em>&nbsp;<span>Kanban</span>
                            </Link>
                          </li>
                          <li className="nk-block-tools-opt">
                            <Link to="/crm_leads" className="btn btn-info" style={{ color: "#fff" }}>
                              <em className="icon ni ni-list"></em>&nbsp;<span>List View</span>
                            </Link>
                          </li>
                          <li className="nk-block-tools-opt">
                            <Link to="/crm_leads_settings" className="btn btn-primary" style={{ color: "#fff" }}>
                              <em className="icon ni ni-setting"></em>&nbsp;<span>Pipeline Settings</span>
                            </Link>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── KPI cards ── */}
              {kpis && (
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
                  {[
                    { label: "All Leads", value: String(kpis.total_leads || 0), sub: fmtMoney(kpis.pipeline_value) + " total value", color: "#2563eb", bg: "#e8eefb", icon: "ni ni-users" },
                    { label: "Open Pipeline", value: String(activeFunnel.reduce(function(total, row) { return total + (Number(row.count) || 0); }, 0)), sub: fmtMoney(kpis.pipeline_value), color: "#2563eb", bg: "#e8eefb", icon: "ni ni-plus-circle", accentSub: true },
                    { label: "Won", value: String(wonTotal.count || kpis.won_qtd_count || 0), sub: fmtMoney(wonTotal.value || kpis.won_qtd_value), color: "#198754", bg: "#e6f4ec", icon: "ni ni-check", accentSub: true },
                    // { label: "Lost", value: String(lostTotal.count || 0), sub: fmtMoney(lostTotal.value), color: "#dc3545", bg: "#f8e8e8", icon: "ni ni-cross", accentSub: true },
                    { label: "Avg Deal Size", value: fmtMoney(kpis.avg_deal_size), sub: "Across " + (kpis.total_leads || 0) + " leads", color: "#d97706", bg: "#fef3c7", icon: "ni ni-sign-dollar" },
                  ].map(function(c, i) {
                    return (
                      <div key={i} style={{ display: "contents" }}>
                        <div className="card card-bordered" style={{ flex: "1 1 190px", minWidth: 180, minHeight: 84, borderRadius: 8 }}>
                          <div className="card-inner" style={{ minHeight: 82, padding: "14px 16px", position: "relative" }}>
                            <div style={{ display: "block", marginBottom: 4, paddingLeft: 48 }}>
                              <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: "#6f6a64" }}>{c.label}</div>
                              <div style={{ width: 36, height: 36, borderRadius: 8, background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", color: c.color, position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)" }}>
                                <em className={"icon " + c.icon} style={{ fontSize: 18 }}></em>
                              </div>
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1.05, marginLeft: 48 }}>{c.value}</div>
                            {c.sub ? <div style={{ fontSize: 11, color: c.accentSub ? c.color : "#7a746d", marginTop: 4, marginLeft: 48, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.sub}</div> : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── Lead Score Formula ── */}
              <div className="example-alert" style={{ marginBottom: 20 }}>
                <div className="alert alert-pro alert-info">
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <em className="icon ni ni-info text-info" style={{ fontSize: 14, lineHeight: 1 }}></em>
                      <strong style={{ fontSize: 14 }}>Lead Score Formula (0 – 100)</strong>
                    </div>
                    Composite of: <strong>Engagement (30%) · Deal value vs. avg (25%) · Stage progression (20%) · Activity recency (15%) · Priority (10%).</strong>
                    <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      <span className="badge badge-pill badge-outline-success">High 70–100</span>
                      <span className="badge badge-pill badge-outline-warning">Medium 40–69</span>
                      <span className="badge badge-pill badge-outline-secondary">Low 0–39</span>
                      <span style={{ fontSize: 12 }}>· Update a lead's score in the&nbsp;<Link to="/crm_leads">All Leads</Link></span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── main two-column layout ── */}
              <div className="nk-block">
              <div className="row g-gs">

                {/* ── LEFT: Funnel Conversion ── */}
                <div className="col-12 col-lg-8">
                  <div className="card card-bordered" style={{ height: "100%" }}>
                    <div className="card-inner">
                      <div className="card-title-group" style={{ marginBottom: 16 }}>
                        <div className="card-title">
                          <h6 className="title">Funnel Conversion</h6>
                          <p>Stage-by-stage conversion rates</p>
                        </div>
                      </div>

                      {loading && (
                        <div className="text-center" style={{ padding: "20px 0" }}>
                          <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                        </div>
                      )}
                      {!loading && activeFunnel.length === 0 && (
                        <p className="text-soft">No active stage data available.</p>
                      )}

                      {/* Active stage bars */}
                      {!loading && activeFunnel.map(function(row) {
                        var stageColor = row.stage.color_hex || "#6b7280";
                        var stageBg = row.stage.bg_hex || "#f3f4f6";
                        var barPct = Math.max(6, Math.floor(row.count / maxCount * 100));
                        var hasConv = row.conv_rate_next != null;
                        return (
                          <div key={row.stage.stage_key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                            {/* stage label */}
                            <div style={{ minWidth: 120, display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{ width: 10, height: 10, borderRadius: "50%", background: stageColor, display: "inline-block", flexShrink: 0 }}></span>
                              <span style={{ fontSize: 12, fontWeight: 600, color: stageColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {row.stage.label}
                              </span>
                            </div>
                            {/* bar */}
                            <div style={{ flex: 1, background: "#f3f4f6", borderRadius: 6, height: 34, overflow: "hidden", position: "relative" }}>
                              <div style={{
                                width: barPct + "%",
                                height: "100%",
                                background: stageBg,
                                borderRadius: 6,
                                display: "flex",
                                alignItems: "center",
                                paddingLeft: 10,
                                minWidth: 60,
                                transition: "width 0.4s ease",
                              }}>
                                <span style={{ color: stageColor, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                                  {row.count} leads · {fmtMoney(row.value)}
                                </span>
                              </div>
                            </div>
                            {/* conversion rate */}
                            <div style={{ minWidth: 72, textAlign: "right" }}>
                              {hasConv ? (
                                <span style={{ fontSize: 11, fontWeight: 700, color: row.conv_rate_next >= 50 ? "#1a7a4a" : row.conv_rate_next >= 25 ? "#d97706" : "#ef4444" }}>
                                  {row.conv_rate_next}%&nbsp;<em className="icon ni ni-arrow-right" style={{ fontSize: 10 }}></em>
                                </span>
                              ) : (
                                <span style={{ fontSize: 11, color: "#d1d5db" }}>—</span>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Won / Lost summary at bottom */}
                      {!loading && (wonRows.length > 0 || lostRows.length > 0) && (
                        <div style={{ borderTop: "1px dashed #e5e7eb", marginTop: 12, paddingTop: 12, display: "flex", gap: 12, flexWrap: "wrap" }}>
                          {wonRows.length > 0 && (
                            <div style={{
                              flex: 1, minWidth: 160,
                              background: "#e6f4ec", borderRadius: 8,
                              border: "1px solid #a7f3d0",
                              padding: "10px 14px",
                              display: "flex", alignItems: "center", gap: 10,
                            }}>
                              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1a7a4a", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <em className="icon ni ni-check-thick" style={{ color: "#fff", fontSize: 16 }}></em>
                              </div>
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#065f46" }}>Won</div>
                                <div style={{ fontSize: 16, fontWeight: 800, color: "#1a7a4a" }}>{wonTotal.count}&nbsp;<span style={{ fontSize: 12, fontWeight: 600 }}>leads · {fmtMoney(wonTotal.value)}</span></div>
                              </div>
                            </div>
                          )}
                          {lostRows.length > 0 && (
                            <div style={{
                              flex: 1, minWidth: 160,
                              background: "#fef2f2", borderRadius: 8,
                              border: "1px solid #fecaca",
                              padding: "10px 14px",
                              display: "flex", alignItems: "center", gap: 10,
                            }}>
                              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <em className="icon ni ni-cross-sm" style={{ color: "#fff", fontSize: 16 }}></em>
                              </div>
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#991b1b" }}>Lost</div>
                                <div style={{ fontSize: 16, fontWeight: 800, color: "#ef4444" }}>{lostTotal.count}&nbsp;<span style={{ fontSize: 12, fontWeight: 600 }}>leads · {fmtMoney(lostTotal.value)}</span></div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── RIGHT: By Source → By Partner ── */}
                <div className="col-12 col-lg-4">

                  {/* By Source — all LEAD_SOURCES shown (0 for missing) */}
                  <div className="card card-bordered" style={{ marginBottom: 16 }}>
                    <div className="card-inner">
                      <div className="card-title-group" style={{ marginBottom: 12 }}>
                        <div className="card-title">
                          <h6 className="title">By Source</h6>
                        </div>
                      </div>
                      {loading && (
                        <div className="text-center" style={{ padding: "20px 0" }}>
                          <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                        </div>
                      )}
                      {!loading && (
                        <div>
                          {allSources.map(function(s, i) {
                            var barW = Math.max(3, Math.floor(s.count / sourceMax * 100));
                            return (
                              <div key={i} style={{ marginBottom: 7 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                                  <span style={{ fontSize: 12, color: s.count === 0 ? "#9ca3af" : "#374151", fontWeight: s.count > 0 ? 600 : 400 }}>{s.source}</span>
                                  <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>
                                    {s.count > 0 ? s.count + " · " + fmtMoney(s.value) : "—"}
                                  </span>
                                </div>
                                <div style={{ height: 4, background: "#f3f4f6", borderRadius: 3 }}>
                                  <div style={{ height: "100%", width: barW + "%", background: s.count === 0 ? "#e5e7eb" : "#046294", borderRadius: 3, transition: "width 0.3s" }}></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* By Partner */}
                  <div className="card card-bordered">
                    <div className="card-inner">
                      <div className="card-title-group" style={{ marginBottom: 12 }}>
                        <div className="card-title">
                          <h6 className="title">By Partner</h6>
                        </div>
                      </div>
                      {loading && (
                        <div className="text-center" style={{ padding: "20px 0" }}>
                          <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                        </div>
                      )}
                      {!loading && byPartner.length === 0 && <p className="text-soft">No partner data.</p>}
                      {!loading && byPartner.length > 0 && (
                        <ul className="list-unstyled" style={{ margin: 0 }}>
                          {byPartner.slice(0, 10).map(function(p, i) {
                            return (
                              <li key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
                                  <span style={{
                                    width: 22, height: 22, borderRadius: "50%",
                                    background: "rgba(2,39,77,0.08)", color: THEME_COLOR,
                                    fontSize: 10, fontWeight: 700,
                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    flexShrink: 0,
                                  }}>{i + 1}</span>
                                  <span style={{ fontSize: 12, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {p.comp_name || p.partner_accountno || "Unassigned"}
                                  </span>
                                </div>
                                <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap", marginLeft: 8 }}>
                                  {p.count} · {fmtMoney(p.value)}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>

                </div>
              </div>
              </div>{/* end nk-block */}

            </div>
          </div>
        </div>
      </div>
    );
  }
}
